import { useState, FC, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    Alert,
    CircularProgress,
    Box,
} from '@mui/material';
import { EventNote as TemplateSetIcon } from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';
import {
    useGetScheduleTemplateSetsQuery,
    useApplyScheduleTemplateSetMutation,
} from '@/store/apiSlice';
import type { ScheduleTemplateSet } from '@/types';

interface CopyTemplateSetDialogProps {
    open: boolean;
    onClose: () => void;
    targetDate: string;
}

const CopyTemplateSetDialog: FC<CopyTemplateSetDialogProps> = ({ open, onClose, targetDate }) => {
    const { LL } = useI18nContext();
    const [selectedSetId, setSelectedSetId] = useState<number | null>(null);

    const { data: templateSets, isLoading } = useGetScheduleTemplateSetsQuery(undefined, {
        skip: !open,
    });

    const [applyTemplateSet, { isLoading: isApplying, error }] =
        useApplyScheduleTemplateSetMutation();

    const sortedSets = useMemo(() => {
        return [...(templateSets ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    }, [templateSets]);

    const handleApply = async () => {
        if (!selectedSetId) return;

        try {
            await applyTemplateSet({ templateSetId: selectedSetId, toDate: targetDate }).unwrap();
            onClose();
            setSelectedSetId(null);
        } catch {
            // handled by mutation state
        }
    };

    const handleClose = () => {
        setSelectedSetId(null);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{LL.scheduleModal.copyTemplateSetTitle()}</DialogTitle>
            <DialogContent>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        mb: 2,
                    }}
                >
                    {LL.scheduleModal.copyTemplateSetDescription()}
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {LL.scheduleModal.templateSetError()}
                    </Alert>
                )}

                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : sortedSets.length > 0 ? (
                    <List sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        {sortedSets.map((set: ScheduleTemplateSet) => (
                            <ListItem key={set.id} disablePadding>
                                <ListItemButton
                                    selected={selectedSetId === set.id}
                                    onClick={() => setSelectedSetId(set.id)}
                                >
                                    <ListItemText
                                        primary={set.name}
                                        secondary={LL.scheduleModal.itemsCount({
                                            count: set.templates?.length ?? 0,
                                        })}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Alert severity="info">{LL.scheduleModal.noTemplateSets()}</Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isApplying}>
                    {LL.common.cancel()}
                </Button>
                <Button
                    onClick={handleApply}
                    variant="contained"
                    disabled={!selectedSetId || isApplying}
                    startIcon={isApplying ? <CircularProgress size={20} /> : <TemplateSetIcon />}
                >
                    {LL.scheduleModal.applyTemplateSetButton()}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CopyTemplateSetDialog;
